import { For, Show, createMemo, createSignal } from "solid-js"
import { useNavigate, useLocation } from "@solidjs/router"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { useTabs } from "@/context/tabs"
import { useLayout } from "@/context/layout"
import { useServer } from "@/context/server"
import { ZdWorkspaceList } from "@/components/zd-workspace-list"
import { ZdRecentConversations } from "@/components/zd-recent-conversations"
import { ZdUserMenu } from "@/components/zd-user-menu"
import collapseIconRaw from "@/assets/self-develop/slide.svg?raw"
import createIconRaw from "@/assets/self-develop/create.svg?raw"
import automationIconRaw from "@/assets/self-develop/automation.svg?raw"
import expertiseIconRaw from "@/assets/self-develop/expertise.svg?raw"
import knowledgeBaseIconRaw from "@/assets/self-develop/knowledgeBase.svg?raw"
import "@/components/zd-left-sidebar.css"

// ─── Icons ───────────────────────────────────────────────────────────────────

const NAV_ICONS: Record<string, string> = {
  create: createIconRaw,
  automation: automationIconRaw,
  expertise: expertiseIconRaw,
  knowledgeBase: knowledgeBaseIconRaw,
}

function NavIcon(props: { name: string }) {
  return <span class="zd-nav-item__icon" innerHTML={NAV_ICONS[props.name] ?? ""} />
}

// 折叠头左侧的展开箭头，与「最近对话」保持一致的形状与旋转方向：
// 展开时旋转 90° 朝下，收起时朝右。
function SectionChevron(props: { expanded: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      class="w-[14px] h-[14px] shrink-0 transition-transform"
      style={{ transform: props.expanded ? "rotate(90deg)" : "rotate(0deg)" }}
    >
      <path d="M6 4L10 8L6 12" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ZdLeftSidebar(props: { onCollapse?: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const tabs = useTabs()
  const layout = useLayout()
  const server = useServer()

  const navItems = [
    { key: "automation", label: "自动化", path: "/automation" },
    { key: "expertise", label: "专家·技能·连接器", path: "/expertise" },
    { key: "knowledgeBase", label: "资料库", path: "/knowledge-base" },
  ]

  const isHome = createMemo(() => {
    // 「新工作任务」实际打开的页面：/{base64}/session（指定目录的新建会话）
    // 或 /new-session（现在用的草稿页）。
    // 根路径 "/" 是主页，已由工作空间标题右侧的主页图标负责高亮，这里不再算进来，
    // 否则停在主页时会出现两个条目同时高亮。
    if (location.pathname === "/") return false
    if (location.pathname === "/new-session") return true
    const parts = location.pathname.split("/").filter(Boolean)
    return parts.length === 2 && parts[1] === "session"
  })

  // 左右结构下「新工作任务」是直接开新建会话（/new-session），主页（/，等同上下结构顶栏
  // 主页图标那个页面）在侧边栏里没有任何入口，所以在「工作空间」标题右侧补一个主页图标。
  const isHomePage = createMemo(() => location.pathname === "/")
  const openHome = () => navigate("/")
  const workspaceCount = createMemo(() => layout.projects.list().length)

  // 只有一个工作空间时折叠没有意义，此时标题保持纯文本、不显示箭头。
  const canCollapseWorkspaces = createMemo(() => workspaceCount() > 1)
  const [workspacesExpanded, setWorkspacesExpanded] = createSignal(true)

  const isActive = (path: string) => {
    const [route, query] = path.split("?")
    if (!location.pathname.startsWith(route)) return false
    if (!query) return true
    return location.search.includes(query)
  }

  const toggleHome = () => {
    // 直接进入「新工作任务」对话框（/new-session 草稿页），不再经过首页。
    const selection = layout.home.selection()
    const dir = selection.directory ?? layout.projects.list()[0]?.worktree ?? "/"
    // 复用同 server + 同目录的已有草稿标签：多次点击「新工作任务」只保持一个新建会话。
    const existing = tabs.store.find(
      (tab) => tab.type === "draft" && tab.server === server.key && tab.directory === dir,
    )
    if (existing) {
      tabs.select(existing)
      return
    }
    void tabs.newDraft({ server: server.key, directory: dir })
  }

  return (
    <aside class="zd-left-sidebar">
      {/* Platform Logo + Collapse button */}
      <div class="zd-brand">
        <img src="/zd-brand-logo.png" alt="智盾·SSAI" class="zd-brand__logo" />
        <Show when={props.onCollapse}>
          <button
            type="button"
            class="zd-sidebar-collapse-btn"
            onClick={() => props.onCollapse?.()}
            aria-label="收起侧边栏"
            innerHTML={collapseIconRaw}
          />
        </Show>
      </div>

      {/* New Task (Home toggle) */}
      <nav class="zd-nav-section">
        <button
          class="zd-nav-item"
          classList={{ "zd-nav-item--active": isHome() }}
          onClick={toggleHome}
        >
          <NavIcon name="create" />
          <span class="zd-nav-item__label">新工作任务</span>
        </button>

        <For each={navItems}>
          {(item) => (
            <button
              class="zd-nav-item"
              classList={{ "zd-nav-item--active": isActive(item.path) }}
              onClick={() => navigate(item.path)}
            >
              <NavIcon name={item.key} />
              <span class="zd-nav-item__label">{item.label}</span>
            </button>
          )}
        </For>
      </nav>

      {/* Recent conversations (collapsible) */}
      <ZdRecentConversations />

      {/* 工作空间标题（可折叠头 / 纯文本）+ 主页入口。
          标题本身在「多个工作空间」时是一个 button，所以主页按钮必须与它平级，
          不能塞进标题内部（button 套 button 是非法结构）。
          主页右侧本身就是完整的项目/工作空间栏，所以停在主页时整块让位，避免两处重复
          展示同一批工作空间。这里用 display:none 而不是卸载：来回切页时
          ZdWorkspaceList 里「哪些工作空间已展开」的状态不会被重置。 */}
      <div
        class="zd-workspace-section"
        classList={{ "zd-workspace-section--hidden": isHomePage() }}
      >
        <div class="zd-nav-section-head">
          <Show
            when={canCollapseWorkspaces()}
            fallback={
              <div class="zd-nav-section-title">
                <span>工作空间</span>
                <Show when={workspaceCount() > 0}>
                  <span class="tabular-nums">（{workspaceCount()}）</span>
                </Show>
              </div>
            }
          >
            <button
              class="zd-nav-section-header"
              onClick={() => setWorkspacesExpanded((v) => !v)}
            >
              <SectionChevron expanded={workspacesExpanded()} />
              <span>工作空间</span>
              <span class="tabular-nums">（{workspaceCount()}）</span>
              <span class="flex-1" />
            </button>
          </Show>
          <button
            type="button"
            class="zd-section-home-btn"
            onClick={openHome}
            aria-label="主页"
            title="主页"
          >
            <IconV2 name="grid-plus" />
          </button>
        </div>
        <Show when={workspacesExpanded() || !canCollapseWorkspaces()}>
          <div class="zd-workspace-container">
            <ZdWorkspaceList />
          </div>
        </Show>
      </div>

      <div class="zd-user-section">
        <ZdUserMenu />
      </div>
    </aside>
  )
}
